import { getURLSearchParamsFromQuery } from '@schibsted-nmp/search-frontend-clients';
import { Pagination as PaginationComponent } from '@warp-ds/react';
import React from 'react';
import { useSearchData } from '../../data/SearchData';
import { useSearchParams } from '../../data/SearchParamsStore';
import { useSubmitSearch } from '../../data/SearchQuery';
import { APP_URL_PARAMS } from '../../state/UrlState';

type PaginationProps = {
    className?: string;
};

export function Pagination(props: PaginationProps) {
    const { className } = props;

    const { searchData } = useSearchData();
    const { searchParams } = useSearchParams();
    const { submitSearch } = useSubmitSearch();

    const firstItemId = searchData?.docs?.[0]?.id;

    const { isPageChangedRef } =
        useScrollToAndFocusFirstItemOnPageChanged(firstItemId);

    const createHref = React.useCallback(
        (page: number) => {
            const urlParams = getURLSearchParamsFromQuery(searchParams);
            if (page === 1) {
                urlParams.delete(APP_URL_PARAMS.page);
            } else {
                urlParams.set(APP_URL_PARAMS.page, page.toString());
            }

            return `?${urlParams}`;
        },
        [searchParams],
    );

    const handlePageChange = React.useCallback(
        (page: number) => {
            submitSearch(
                (urlParams) => {
                    if (page === 1) {
                        urlParams.delete('page');
                    } else {
                        urlParams.set('page', page.toString());
                    }
                },
                { keepPageParam: true },
            );

            isPageChangedRef.current = true;
        },
        [isPageChangedRef, submitSearch],
    );

    const pagingInfo = searchData?.metadata?.paging;
    const showPaging = pagingInfo && pagingInfo.last > 0;

    if (!showPaging) {
        return null;
    }

    return (
        <PaginationComponent
            className={className}
            createHref={createHref}
            currentPage={pagingInfo.current}
            lastPage={pagingInfo.last}
            onChange={handlePageChange}
        />
    );
}

function useScrollToAndFocusFirstItemOnPageChanged(firstItemId?: string) {
    const isPageChangedRef = React.useRef(false);

    React.useEffect(() => {
        if (isPageChangedRef.current && firstItemId) {
            const firstAdLink = document.getElementById(firstItemId);

            window.scrollTo({
                behavior: 'auto',
                top: firstAdLink?.getBoundingClientRect().top ?? 0,
            });

            setTimeout(function waitForDomUpdateHack() {
                firstAdLink?.focus();
            }, 200);
        }

        isPageChangedRef.current = false;
    }, [firstItemId]);

    return { isPageChangedRef };
}
